import { useEffect, useRef, useCallback, useState, useLayoutEffect } from "react";
import Filter from "./components/Filter";
import { Box, Typography } from "@mui/material";
import Post from "./components/Post";
import { useGetPostsQuery } from "@/store/api/postApi";
import { IPost } from "@/store/api/postApiData";
import { styles } from "./stylesHome";
import { useLocation, useOutletContext } from "react-router-dom";
import "draft-js/dist/Draft.css";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "@/store/store";
import { useAppSelector } from "@/hooks/useAppSelector";
import { setParams, resetPage } from "@/store/slice/postParamsSlice";
import CustomLoader from "@/components/UI/CustomLoader/CustomLoader";
import NewPostsBanner from "./components/NewPostsBanner";
import { useFeedNewPostsCount } from "@/hooks/useFeedNewPostsCount";

const POSTS_LIMIT = 30;

type OutletContextType = {
  scrollRef: React.RefObject<HTMLDivElement>;
};

const Home = () => {
  const dispatch = useDispatch();
  const params = useSelector((state: RootState) => state.postParams);
  const [allPosts, setAllPosts] = useState<IPost[]>([]);
  const [hasMore, setHasMore] = useState(true);
  const [sortDirection, setSortDirection] = useState<"asc" | "desc">("desc");
  const location = useLocation();
  const observer = useRef<IntersectionObserver | null>(null);
  const loadMoreRef = useRef<HTMLDivElement | null>(null);
  const { postLoading, editingPost, draftLoading } = useSelector(
    (state: RootState) => state.loading
  );
  const { subscriptionStatus } = useAppSelector((state) => state.auth);
  const [hasLoadedOnce, setHasLoadedOnce] = useState(false);
  const hasPremium = subscriptionStatus === "ACTIVE";
  const [isParamsChanging, setIsParamsChanging] = useState(false);

  const updatedPosts = useSelector((state: RootState) => state.updatedPosts.posts);
  const { scrollRef } = useOutletContext<OutletContextType>();
  const hasUserScrolledRef = useRef(false);
  const { count: newPostsCount, resetCount: resetNewPostsCount } = useFeedNewPostsCount();

  useEffect(() => {
    setIsParamsChanging(true);
  }, [params]);

  useEffect(() => {
    const el = scrollRef?.current;
    if (!el) return;

    const onScroll = () => {
      hasUserScrolledRef.current = true;
      el.removeEventListener("scroll", onScroll);
    };

    el.addEventListener("scroll", onScroll);
    return () => el.removeEventListener("scroll", onScroll);
  }, [scrollRef]);

  useEffect(() => {
    if (!updatedPosts) return;

    setAllPosts((prev) => prev.map((p) => updatedPosts[p.uid] || p));
  }, [updatedPosts]);

  const searchParams = new URLSearchParams(location.search);
  const textFromUrl = searchParams.get("text") ?? "";
  const authorFromUrl = searchParams.get("author") ?? "";
  const effectiveParams = {
    ...params,
    text: textFromUrl,
    author: authorFromUrl || params.author,
  };

  const {
    data: posts,
    error,
    isLoading,
    isFetching,
    refetch,
  } = useGetPostsQuery(effectiveParams, {
    refetchOnMountOrArgChange: true,
    refetchOnFocus: true,
    refetchOnReconnect: true,
  });

  useEffect(() => {
    if (!posts) return;
    setIsParamsChanging(false);
    const postsArray = posts.data;
    if (params.page === 1) {
      setAllPosts(postsArray);
      resetNewPostsCount();
    } else {
      setAllPosts((prev) => {
        const updatedPosts = prev.map((p) => {
          const updated = postsArray.find((np) => np.uid === p.uid);
          return updated ? updated : p;
        });

        const newPosts = postsArray.filter((p) => !prev.some((ap) => ap.uid === p.uid));
        return [...updatedPosts, ...newPosts];
      });
    }
    setHasMore(posts.meta.total > POSTS_LIMIT * params.page);
  }, [posts, params.page]);

  const isFetchingRef = useRef(isFetching);
  const hasMoreRef = useRef(hasMore);
  const paramsRef = useRef(params);
  useEffect(() => {
    isFetchingRef.current = isFetching;
  }, [isFetching]);
  useEffect(() => {
    hasMoreRef.current = hasMore;
  }, [hasMore]);
  useEffect(() => {
    paramsRef.current = params;
  }, [params]);

  const loadNextPage = useCallback(() => {
    if (!isFetchingRef.current && hasMoreRef.current && paramsRef.current.page > 0) {
      dispatch(setParams({ page: paramsRef.current.page + 1 }));
    }
  }, [dispatch]);

  useEffect(() => {
    dispatch(setParams({ order: sortDirection, page: 1 }));
  }, [sortDirection, dispatch]);

  useEffect(() => {
    const paramsQuery = new URLSearchParams(location.search);
    const postId = paramsQuery.get("post");

    if (postId && allPosts.length > 0) {
      const timeoutId = setTimeout(() => {
        const el = document.getElementById(`post-${postId}`);
        if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 0);
      return () => clearTimeout(timeoutId);
    }
  }, [location.search, allPosts]);
  const onRefreshPosts = () => {
    dispatch(resetPage());
    refetch();
  };

  const handleShowNewPosts = () => {
    resetNewPostsCount();
    scrollRef?.current?.scrollTo({ top: 0, behavior: "smooth" });
    onRefreshPosts();
  };

  const handleDeletePost = (uid: string) => {
    setAllPosts((prev) => prev.filter((post) => post.uid !== uid));
  };

  useLayoutEffect(() => {
    if (!scrollRef?.current || !location.state?.forceScrollTop) return;

    scrollRef.current.scrollTo(0, 0);
    hasUserScrolledRef.current = false;

    if (params.page !== 1) {
      dispatch(resetPage());
    } else if (posts !== undefined) {
      refetch();
    }

    window.history.replaceState({ ...location.state, forceScrollTop: false }, "");
  }, [location.key]);

  useEffect(() => {
    if (posts) {
      setHasLoadedOnce(true);
    }
  }, [posts]);

  const isInitialLoading = !hasLoadedOnce && (isLoading || isFetching);

  const isEmpty = hasLoadedOnce && !isFetching && !isParamsChanging && allPosts.length === 0;

  useEffect(() => {
    // the sentinel only mounts once the initial-loading/error/empty states
    // are past, so the effect must re-run when any of them flips to (re)attach
    if (isInitialLoading || error || isEmpty || !loadMoreRef.current) return;

    const observerCallback = (entries: IntersectionObserverEntry[]) => {
      if (entries[0].isIntersecting && hasUserScrolledRef.current) {
        loadNextPage();
      }
    };

    observer.current = new IntersectionObserver(observerCallback, {
      root: scrollRef.current,
      rootMargin: "100px",
      threshold: 0.1,
    });

    observer.current.observe(loadMoreRef.current);

    return () => observer.current?.disconnect();
  }, [loadNextPage, isInitialLoading, error, isEmpty]);

  const lastAppliedTextRef = useRef<string | null>(null);

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const textFromUrl = searchParams.get("text") ?? "";
    const authorFromUrl = searchParams.get("author") ?? "";

    if (lastAppliedTextRef.current === textFromUrl) return;

    lastAppliedTextRef.current = textFromUrl;

    dispatch(setParams({ text: textFromUrl, author: authorFromUrl, page: 1 }));
  }, [location.search]);

  return (
    <Box sx={{ height: "100%", width: "100%", minWidth: 0 }}>
      <Box sx={{ position: "sticky", top: "54px", zIndex: 20 }}>
        <Filter
          key={`filters:${textFromUrl}|${authorFromUrl}`}
          setParams={(newParams) => dispatch(setParams(newParams))}
          sortDirection={sortDirection}
          setSortDirection={setSortDirection}
          onRefreshPosts={onRefreshPosts}
          limit={POSTS_LIMIT}
          isPremium={hasPremium}
          initialTimeRange={textFromUrl || authorFromUrl ? "All" : "1 day"}
        />
        <NewPostsBanner count={newPostsCount} onClick={handleShowNewPosts} />
      </Box>
      {isInitialLoading ? (
        <Box
          sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: "80vh" }}
        >
          <CustomLoader />
        </Box>
      ) : error ? (
        <Box
          sx={{
            padding: "1rem",
            textAlign: "center",
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            height: "70vh",
          }}
        >
          <Typography color="text.secondary" sx={{ fontSize: "28px" }}>
            {(error as any)?.data?.message || "Something went wrong"}
          </Typography>
          <Typography color="text.secondary" sx={{ fontSize: "24px" }}>
            {(error as any)?.status || (error as any)?.data?.statusCode || "Error"}
          </Typography>
        </Box>
      ) : isEmpty ? (
        <Box
          sx={{
            padding: "1rem",
            textAlign: "center",
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            height: "70vh",
          }}
        >
          <Typography color="text.secondary" sx={{ fontSize: "28px" }}>
            No posts
          </Typography>
          <Typography color="text.secondary" sx={{ fontSize: "24px" }}>
            There are no posts to display.
          </Typography>
        </Box>
      ) : (
        <Box sx={styles.home}>
          {!editingPost.loading &&
            (isFetching || postLoading || draftLoading) &&
            params.page === 1 && (
              <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
                <CustomLoader size={34} />
              </Box>
            )}
          <Box sx={{ display: "flex", flexDirection: "column" }}>
            {allPosts.map((post: IPost) => {
              const isEditingThisPost = editingPost.loading && editingPost.postId === post.uid;

              return (
                <Post
                  post={post}
                  key={post.uid}
                  onDelete={handleDeletePost}
                  isEditing={isEditingThisPost}
                />
              );
            })}
            <div ref={loadMoreRef} style={{ height: "50px" }} />
            {isFetching && params.page > 1 && allPosts.length > 0 && (
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  padding: 2,
                  mt: "-40px",
                }}
              >
                <CustomLoader />
              </Box>
            )}
            {!hasMore && allPosts.length > 0 && (
              <Box sx={{ textAlign: "center", padding: 2, color: "text.secondary", mt: "-40px" }}>
                No more posts
              </Box>
            )}
          </Box>
        </Box>
      )}
    </Box>
  );
};
export default Home;
